import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function LensEquationCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Lens Equation Calculator", url: "https://calclab.app/calculator/lens-equation" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Lens Equation Calculator",
      "Calculate focal length, object distance, or image distance using 1/f = 1/do + 1/di. Free optics calculator.",
      "https://calclab.app/calculator/lens-equation",
      lang
    )
  ]);
  const [objectDistance, setObjectDistance] = useState("");
  const [imageDistance, setImageDistance] = useState("");
  const [focalLength, setFocalLength] = useState("");
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const do_val = parseFloat(objectDistance);
    const di = parseFloat(imageDistance);
    const f = parseFloat(focalLength);

    let count = 0;
    if (!isNaN(do_val)) count++;
    if (!isNaN(di)) count++;
    if (!isNaN(f)) count++;

    if (count < 2) {
      toast.error("Enter any 2 values to solve for the 3rd");
      return;
    }

    let result: any = {};
    if (isNaN(f)) {
      result.f = 1 / (1/do_val + 1/di);
      result.label = "Focal Length";
    } else if (isNaN(di)) {
      result.di = 1 / (1/f - 1/do_val);
      result.label = "Image Distance";
    } else {
      result.do = 1 / (1/f - 1/di);
      result.label = "Object Distance";
    }

    setResult(result);
    toast.success("Calculation complete!");
  };

  return (
    <PageLayout calculatorName="Lens Equation Calculator" calculatorPath="/calculator/lens-equation">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Lens Equation Calculator</h1>
          <p className="text-muted-foreground">Thin lens calculations</p>
        </div>
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>1/f = 1/d_o + 1/d_i</CardTitle>
            <CardDescription>Enter any 2 values</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Object Distance d_o (cm)</Label>
                <Input type="number" step="any" value={objectDistance} onChange={(e) => setObjectDistance(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Image Distance d_i (cm)</Label>
                <Input type="number" step="any" value={imageDistance} onChange={(e) => setImageDistance(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Focal Length f (cm)</Label>
                <Input type="number" step="any" value={focalLength} onChange={(e) => setFocalLength(e.target.value)} />
              </div>
            </div>
            <Button onClick={calculate} className="w-full">
              <Calculator className="mr-2 h-4 w-4" />Calculate
            </Button>
            {result && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-3xl font-bold text-primary break-all overflow-wrap-anywhere">{(result.f || result.di || result.do).toFixed(2)} cm</p>
                        <p className="text-sm text-muted-foreground mt-2">{result.label}</p>
                      </div>
                      <CopyResultButton value={`${(result.f || result.di || result.do).toFixed(2)} cm`} />
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["lens-equation"]} trigger={result.f || result.di || result.do} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
